import { computed, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { i18n } from '@/utils/i18n';
import { getMaterialAlterCodesForSalesDocuments } from '@/services/api/materials';
import {
  getMaterialDiscounts,
  getMaterialPriceFromPriceCatalog,
  getMaterialVatFactorForTraderSite,
  getPricingPolicyByCustomerId,
} from '@/services/api/prices';
import { applyDiscounts, roundValue, stringifyAppliedDiscounts } from '@/utils/prices';
import { helpers, maxValue, minValue, required } from '@vuelidate/validators';
import { v4 as uuidv4 } from 'uuid';

import { RecordMode } from '@/types/Base';
import {
  eDiscountOrigin,
  type DocumentDetail,
  type DocumentDetailDiscount,
  type SalesDocument,
} from '@/types/Documents';
import type { MaterialAlterCodeForSalesDocument } from '@/types/Materials';
import type { PricingPolicy } from '@/types/PricingTypes';
import type { FilterGroupItems } from '@/types/Groups';
import { getRangeDetailsByCustomerTraderSite } from '@/services/api/materials';
import type { DocumentType } from '@/types/DocumentTypes';
import type { MeasurementUnit } from '@/types/Materials';
import { getDocumentTypeById } from '@/services/api/documents';

export interface DynamicQuantityField {
  unitIndex: number;
  quantity: number;
  measurementUnitId: string;
  label: string;
}

export const useSalesDocumentsStore = defineStore('saleDocument-store', () => {
  const salesDocument = ref<SalesDocument>();
  const modifiedSalesDocument = ref<SalesDocument>();
  const docDetailLines = ref<DocumentDetail[]>([]);
  const customerPricingPolicy = ref<PricingPolicy | null>();
  const isModified = ref(false);
  const clearForm = ref(false);
  const clearDetails = ref(false);
  const rangeLineIds = ref<string[]>([]);
  const activeFilters = ref<FilterGroupItems[]>([]);

  // Filter management functions
  const setActiveFilters = (filters: FilterGroupItems[]) => {
    activeFilters.value = filters;
  };

  const addFilter = (filter: FilterGroupItems) => {
    const existingIndex = activeFilters.value.findIndex((f) => f.id === filter.id);
    if (existingIndex === -1) {
      activeFilters.value.push(filter);
    }
  };

  const removeFilter = (filterId: string) => {
    const index = activeFilters.value.findIndex((f) => f.id === filterId);
    if (index !== -1) {
      activeFilters.value.splice(index, 1);
    }
  };

  const clearAllFilters = () => {
    activeFilters.value = [];
  };

  const hasModifiedDetails = computed(() => {
    return (
      docDetailLines.value.length > 0 && docDetailLines.value.some((d) => d.isModified == true)
    );
  });

  watch(
    () => modifiedSalesDocument.value,
    (newData) => {
      isModified.value =
        JSON.stringify(newData) !== JSON.stringify(salesDocument.value) || hasModifiedDetails.value;
    },
    { deep: true, immediate: true }
  );

  watch(
    () => modifiedSalesDocument.value?.customerId,
    async (newCustomerId) => {
      if (newCustomerId)
        customerPricingPolicy.value = await getPricingPolicyByCustomerId(newCustomerId);
    },
    {
      immediate: true,
    }
  );

  watch(
    () => hasModifiedDetails.value,
    (hasModified) => {
      isModified.value =
        JSON.stringify(modifiedSalesDocument.value) !== JSON.stringify(salesDocument.value) ||
        hasModified;
    }
  );

  const oldVatLevelId = ref('');
  watch(
    () => modifiedSalesDocument.value?.documentHeader.billingAddress?.vatLevelId,
    async (newValue, oldValue) => {
      if (newValue === undefined && oldValue !== undefined) oldVatLevelId.value = oldValue;
      if (newValue != undefined && newValue !== oldVatLevelId.value) {
        for (let line of docDetailLines.value) {
          line = await getMaterialVatFactor(line);
          line = vatFactorChanged(line);
          await saveDocumentLineInStore(line);
        }
      }
    }
  );

  // Searching/Selecting material Alter codes
  async function searchMaterialAlterCodes(
    searchText: string,
    materialAlterCodeId?: string,
    groupItemIds?: string[]
  ): Promise<MaterialAlterCodeForSalesDocument[]> {
    const params = {
      query: searchText,
      sortColumn: 'material.id',
      sortAscending: true,
      materialAlterCodeId: materialAlterCodeId,
      pricingPolicyId: customerPricingPolicy.value?.id,
      groupItemIds,
    };
    const res = await getMaterialAlterCodesForSalesDocuments(params);

    if (!res) return [];
    return res;
  }

  async function getMaterialPropertiesFromCustomerPricingPolicy(line: DocumentDetail) {
    if (!customerPricingPolicy.value) return line;
    const materialFromPolicy = await getMaterialPriceFromPriceCatalog(
      line?.materialId ?? '',
      customerPricingPolicy.value.id
    );
    line.allowDiscount = materialFromPolicy?.allowDiscount || true;
    line.material = {
      allowManualPrice: !!materialFromPolicy?.allowManualPrice,
      allowZeroValue: !!materialFromPolicy?.allowZeroValue,
      image: materialFromPolicy?.image,
    };

    const materialPriceFromPolicy = materialFromPolicy?.price;
    if (!materialPriceFromPolicy) return line;

    line.unitPrice = materialPriceFromPolicy;
    return line;
  }

  async function getMaterialDiscountsFromCustomerPricingPolicy(line: DocumentDetail) {
    if (customerPricingPolicy.value?.pricingType != 1) return line;

    if (!line.unitPrice) return line;

    const pricingPolicyMatrixDetails = await getMaterialDiscounts(
      customerPricingPolicy.value.id,
      line.materialId,
      modifiedSalesDocument.value?.documentHeader?.billingAddressId
    );

    if (!pricingPolicyMatrixDetails) return line;

    line.appliedDiscounts = stringifyAppliedDiscounts(
      pricingPolicyMatrixDetails.map(({ id, discountSequence, discountType, value }) => ({
        id,
        discountSequence,
        discountType,
        value,
      }))
    );

    const discountedPrice = applyDiscounts(
      line.unitPrice,
      pricingPolicyMatrixDetails.map(({ id, discountSequence, discountType, value }) => ({
        id,
        discountSequence,
        discountType,
        value,
      }))
    );

    if (discountedPrice) {
      line.unitPrice = discountedPrice;
    }

    return line;
  }

  async function getMaterialVatFactor(line: DocumentDetail) {
    if (
      !modifiedSalesDocument.value ||
      !modifiedSalesDocument.value.documentHeader.billingAddressId
    )
      return line;
    const vatFactor = await getMaterialVatFactorForTraderSite(
      modifiedSalesDocument.value.documentHeader.billingAddressId,
      line.materialId
    );
    line.vatFactorValue = vatFactor?.vatFactorValue;
    if (!vatFactor) return line;
    line.vatFactor = vatFactor;
    line.vatFactorId = vatFactor.id;
    return line;
  }

  // Discount & Price Calculations
  function calculateTotalBeforeDiscount(line: DocumentDetail) {
    return roundValue(line.unitPrice * (line.totalQuantity || 0));
  }

  function calculateUnitDiscount(line: DocumentDetail) {
    return roundValue((line.unitPrice * line.percentageDiscount) / 100);
  }

  function calculateTotalDiscount(line: DocumentDetail) {
    return roundValue(line.unitDiscount * (line.totalQuantity || 0));
  }

  function calculateNetTotal(line: DocumentDetail) {
    return roundValue(line.totalBeforeDiscount - line.totalDiscount);
  }

  function calculateLineVatTotal(line: DocumentDetail) {
    return roundValue(((line.vatFactorValue ?? 0) / 100) * line.netTotal);
  }

  function calculateGrossTotal(line: DocumentDetail) {
    return roundValue(line.netTotal + line.lineVatTotal);
  }

  function priceOrQuantityChange(line: DocumentDetail, valueChanged: 'unitPrice' | 'quantity') {
    if (valueChanged == 'unitPrice') {
      line.appliedDiscounts = null;
      if (line.percentageDiscount) {
        line.unitDiscount = calculateUnitDiscount(line);
      }
    }
    line.totalQuantity = calculateTotalQuantity(line);
    line.totalDiscount = calculateTotalDiscount(line);
    line.totalBeforeDiscount = calculateTotalBeforeDiscount(line);
    line.netTotal = calculateNetTotal(line);
    line.lineVatTotal = calculateLineVatTotal(line);
    line.grossTotal = calculateGrossTotal(line);
    return line;
  }

  function discountValueChange(line: DocumentDetail, error: string) {
    if (error && error.length > 0) return line;

    if (line.unitDiscount > line.unitPrice) line.unitDiscount = line.unitPrice;
    else if (line.unitDiscount == 0) line.percentageDiscount = 0;
    else line.percentageDiscount = roundValue((line.unitDiscount / line.unitPrice) * 100);

    line.totalBeforeDiscount = calculateTotalBeforeDiscount(line);
    line.totalDiscount = calculateTotalDiscount(line);
    line.netTotal = calculateNetTotal(line);
    line.lineVatTotal = calculateLineVatTotal(line);
    line.grossTotal = calculateGrossTotal(line);
    return line;
  }

  function discountPercentageChange(line: DocumentDetail, error: string) {
    if (error && error.length > 0) return line;

    if (!line.percentageDiscount || line.percentageDiscount <= 0) {
      line.percentageDiscount = 0;
      line.unitDiscount = 0;
    } else if (line.percentageDiscount >= 100) {
      line.percentageDiscount = 100;
      line.unitDiscount = line.unitPrice;
    } else line.unitDiscount = calculateUnitDiscount(line);
    line.totalDiscount = calculateTotalDiscount(line);
    line.totalBeforeDiscount = calculateTotalBeforeDiscount(line);
    line.netTotal = calculateNetTotal(line);
    line.lineVatTotal = calculateLineVatTotal(line);
    line.grossTotal = calculateGrossTotal(line);

    return line;
  }

  function grossTotalChange(line: DocumentDetail) {
    if (!(line.totalQuantity || 0) && !line.grossTotal) return line;
    line.appliedDiscounts = null;

    line.totalBeforeDiscount = calculateTotalBeforeDiscount(line);

    const grossDiff = roundValue(
      line.totalBeforeDiscount +
        (line.totalBeforeDiscount * (line.vatFactorValue ?? 0)) / 100 -
        line.grossTotal
    );
    const netDiff = roundValue(grossDiff / (1 + (line.vatFactorValue ?? 0) / 100));

    line.totalDiscount = netDiff;
    line.percentageDiscount = roundValue((line.totalDiscount / line.totalBeforeDiscount) * 100);
    line.unitDiscount = calculateUnitDiscount(line);

    line.netTotal = calculateNetTotal(line);
    line.lineVatTotal = calculateLineVatTotal(line);
    line.grossTotal = calculateGrossTotal(line);
    return line;
  }

  function netTotalChange(line: DocumentDetail) {
    if (!(line.totalQuantity || 0) && !line.netTotal) return line;
    line.appliedDiscounts = null;

    line.totalBeforeDiscount = calculateTotalBeforeDiscount(line);
    line.totalDiscount = roundValue(line.totalBeforeDiscount - line.netTotal);
    line.percentageDiscount = roundValue((line.totalDiscount / line.totalBeforeDiscount) * 100);
    line.unitDiscount = calculateUnitDiscount(line);

    line.netTotal = calculateNetTotal(line);
    line.lineVatTotal = calculateLineVatTotal(line);
    line.grossTotal = calculateGrossTotal(line);
    return line;
  }

  function vatFactorChanged(line: DocumentDetail) {
    line.lineVatTotal = calculateLineVatTotal(line);
    line.grossTotal = calculateGrossTotal(line);
    return line;
  }

  function updateExistsNotExistsFlags(
    line: DocumentDetail,
    changedProperty: 'exists' | 'notExists'
  ): DocumentDetail {
    if (line[changedProperty] === true) {
      const oppositeProperty = changedProperty === 'exists' ? 'notExists' : 'exists';
      line[oppositeProperty] = false;
    }

    return line;
  }

  // Save and update Lines
  function initNewDocumentLine() {
    return {
      id: uuidv4(),
      materialId: '',
      materialName: '',
      materialCode: '',
      materialAlterCodeId: '',
      documentHeaderId: modifiedSalesDocument.value?.documentHeader.id || '',
      warehouseId: modifiedSalesDocument.value?.documentHeader.warehouseId || '',
      warehouse: modifiedSalesDocument.value?.documentHeader.warehouse,
      vatFactorValue: 0,
      vatFactorId: '',
      lineVatTotal: 0,
      percentageDiscount: 0,
      unitDiscount: 0,
      totalDiscount: 0,
      quantity: 0,
      quantity2: 0,
      quantity3: 0,
      totalQuantity: 0,
      isFromManager: true,
      isModified: true,
      material: { allowManualPrice: false, allowZeroValue: false, image: '' },
      recordMode: RecordMode.Added,
    } as DocumentDetail;
  }

  function updateDocLines(line: DocumentDetail, docLines: DocumentDetail[]) {
    const index = docLines.findIndex((x) => x.id === line.id);

    if (index == -1) return docLines;
    line.isModified = true;
    docLines[index] = { ...docLines[index], ...line };

    return docLines;
  }

  async function saveDocumentLineInStore(line: DocumentDetail, executeDelete: boolean = false) {
    if (!modifiedSalesDocument.value) return;
    if (!line.recordMode) line.recordMode = RecordMode.Modified; // indicates if the object needs to be updated on the backend
    line.isModified = false; // indicates if there are unfinallized changes on the table line
    line.documentDetailDiscounts = updateLineDiscounts(line);

    if (!modifiedSalesDocument.value.documentHeader.documentDetails)
      modifiedSalesDocument.value.documentHeader.documentDetails = [];

    const details = modifiedSalesDocument.value.documentHeader.documentDetails;
    const index = details.findIndex((x) => x.id === line.id);

    if (index === -1) {
      details.unshift(line);
    } else {
      if (line.recordMode === RecordMode.Added && executeDelete) {
        const index = details.findIndex((x) => x.id === line.id);
        if (index == -1) return;
        details.splice(index, 1);
      } else {
        if (line.materialAlterCode.alterCode) {
          line.materialName = line.materialAlterCode.alterCode.name;
          line.materialCode = line.materialAlterCode.alterCode.code;
        }
        details[index] = { ...details[index], ...line };
      }
    }
    updateHeaderTotals();
  }

  function updateLineDiscounts(
    line: DocumentDetail,
    discountOrigin = eDiscountOrigin.Line,
    headerDiscount?: number
  ): DocumentDetailDiscount[] {
    if (line.recordMode === RecordMode.Deleted) return line.documentDetailDiscounts ?? [];

    const newDiscount = {
      id: uuidv4(),
      discountOrigin: discountOrigin,
      documentDetailId: line.id,
      discountUnitValue: roundValue(
        (headerDiscount ?? line.unitDiscount) / (line.totalQuantity || line.quantity)
      ),
      discountTotalValue: headerDiscount ?? line.unitDiscount ?? 0,
      discountPercentage: line.percentageDiscount || 0,
      sequence: 0,
    };

    if (discountOrigin === eDiscountOrigin.Line) {
      const detailLineDiscountSum = line.documentDetailDiscounts?.reduce(
        (s, d) => s + (d.discountTotalValue || 0),
        0
      );
      if (line.unitDiscount === detailLineDiscountSum) return line.documentDetailDiscounts ?? [];
      newDiscount.sequence = 1;
      return [newDiscount];
    }

    const documentDetailDiscounts = line.documentDetailDiscounts || [];
    const index = documentDetailDiscounts.findIndex(
      (discount) => discount.discountOrigin === discountOrigin
    );

    if (index !== -1) {
      newDiscount.id = documentDetailDiscounts[index].id;
      newDiscount.sequence = documentDetailDiscounts[index].sequence;
      newDiscount.discountTotalValue = roundValue(
        newDiscount.discountTotalValue + documentDetailDiscounts[index].discountTotalValue
      );
      newDiscount.discountPercentage = roundValue(
        newDiscount.discountPercentage + documentDetailDiscounts[index].discountPercentage
      );
      newDiscount.discountUnitValue = roundValue(
        newDiscount.discountTotalValue / (line.totalQuantity || line.quantity)
      );
      documentDetailDiscounts[index] = newDiscount;
    } else {
      newDiscount.sequence = documentDetailDiscounts.length + 1;
      documentDetailDiscounts.push(newDiscount);
    }

    return documentDetailDiscounts;
  }

  // Validation
  function createLineRules(line: DocumentDetail | null) {
    if (!line?.id) return {};
    return {
      materialAlterCodeId: {
        required: helpers.withMessage('Material selection is required', required),
      },
      totalQuantity: { docLineQuantityRule },
      unitPrice: {
        docLineUnitPriceRule,
      },
      grossTotal: { required: helpers.withMessage('Gross Total is required', required) },
      discountValue: {
        minValue: minValue(0),
        maxValue: maxValue(line.unitPrice || 0),
      },
      discountPercentage: {
        minValue: minValue(0),
        maxValue: maxValue(100),
      },
    };
  }

  function createLineValidationModel(line: DocumentDetail | null) {
    return {
      materialAlterCodeId: line?.materialAlterCodeId || '',
      totalQuantity: line?.totalQuantity || '',
      unitPrice: line?.unitPrice || '',
      grossTotal: line?.grossTotal || '',
      discountValue: line?.unitDiscount || 0,
      discountPercentage: line?.percentageDiscount || 0,
      exists: line?.exists ?? null,
      notExists: line?.notExists ?? null,
      material: line?.material ?? null,
    };
  }

  function updateHeaderTotals() {
    if (!modifiedSalesDocument.value || !modifiedSalesDocument.value.documentHeader.documentDetails)
      return;
    const totalQty = modifiedSalesDocument.value.documentHeader.documentDetails
      .filter((d) => d.recordMode != RecordMode.Deleted)
      .reduce((s, d) => s + (d.totalQuantity || d.quantity || 0), 0);
    const totalGross = modifiedSalesDocument.value.documentHeader.documentDetails
      .filter((d) => d.recordMode != RecordMode.Deleted)
      .reduce((s, d) => s + (d.grossTotal || 0), 0);
    const totalVat = modifiedSalesDocument.value.documentHeader.documentDetails
      .filter((d) => d.recordMode != RecordMode.Deleted)
      .reduce((s, d) => s + (d.lineVatTotal || 0), 0);
    const totalNet = modifiedSalesDocument.value.documentHeader.documentDetails
      .filter((d) => d.recordMode != RecordMode.Deleted)
      .reduce((s, d) => s + (d.netTotal || 0), 0);
    const totalDiscount = modifiedSalesDocument.value.documentHeader.documentDetails
      .filter((d) => d.recordMode != RecordMode.Deleted)
      .reduce((s, d) => s + (d.totalDiscount || 0), 0);

    modifiedSalesDocument.value.documentHeader.totalQty = roundValue(totalQty);
    modifiedSalesDocument.value.documentHeader.totalGross = roundValue(totalGross);
    modifiedSalesDocument.value.documentHeader.totalVat = roundValue(totalVat);
    modifiedSalesDocument.value.documentHeader.totalNetValue = roundValue(totalNet);
    modifiedSalesDocument.value.documentHeader.totalDiscounts = roundValue(totalDiscount);
  }

  const docLineQuantityRule = helpers.withMessage(
    "Value must respect DocumentType Allow Zero Quantity and be less than DocumentType's Max Line Quantity",
    (value: number) => {
      const qty = Number(value);
      const allowZeroQty = !!modifiedSalesDocument.value?.documentHeader.documentType?.allowZeroQty;
      const maxLineQty =
        modifiedSalesDocument.value?.documentHeader.documentType?.maxDetailQty ?? 0;
      return (allowZeroQty ? qty >= 0 : qty > 0) && (maxLineQty === 0 || qty <= maxLineQty);
    }
  );

  const docLineUnitPriceRule = helpers.withMessage(
    "Value must be greater than 0 and less than DocumentType's Max Line Unit Price",
    (value, siblingState) => {
      const price = Number(value);
      const lineAllowZero = !!siblingState.material.allowZeroValue;
      const typeAllowZero =
        !!modifiedSalesDocument.value?.documentHeader.documentType?.allowZeroValues;

      const allowZero = lineAllowZero && typeAllowZero;
      const maxLineUnitPrice =
        modifiedSalesDocument.value?.documentHeader.documentType?.maxDetailUnitPrice ?? 0;
      return (
        (allowZero ? price >= 0 : price > 0) &&
        (maxLineUnitPrice === 0 || price <= maxLineUnitPrice)
      );
    }
  );

  const priceZeroRule = helpers.withMessage('Invalid value', (value, siblingState) => {
    const price = Number(value);
    const lineAllowZero = !!siblingState.material.allowZeroValue;
    const typeAllowZero =
      !!modifiedSalesDocument.value?.documentHeader.documentType?.allowZeroValues;

    const allowZero = lineAllowZero && typeAllowZero;
    return allowZero ? price >= 0 : price > 0;
  });

  const materialActiveCatalogRule = helpers.withMessage(
    i18n.global.t('messages.errors.materialNotInActiveCatalog'),
    (value, siblingState) => {
      if (
        modifiedSalesDocument.value?.documentHeader.documentType?.allowManualPrices === true ||
        !value
      ) {
        return true;
      }

      const unitPrice = siblingState.unitPrice;
      return unitPrice !== null && unitPrice !== undefined && Number(unitPrice) > 0;
    }
  );

  const discountNotMoreThanUnitPrice = helpers.withMessage(
    'Discount cannot exceed the Unit Price',
    (value, siblingState) => {
      if (value == null || value === '') return true;
      const discount = Number(value);
      const unitPrice = Number(siblingState?.unitPrice ?? 0);
      return discount <= unitPrice;
    }
  );

  function processSiblingMaterialAlterCodes(
    documentDetail: DocumentDetail,
    selectedMaterialAlterCode: MaterialAlterCodeForSalesDocument,
    totalUnits: number
  ): DocumentDetail {
    documentDetail.measurementUnit1Id = '';
    documentDetail.measurementUnit2Id = '';
    documentDetail.measurementUnit3Id = '';
    documentDetail.relationFactor1 = undefined;
    documentDetail.relationFactor2 = undefined;
    documentDetail.relationFactor3 = undefined;

    if (totalUnits <= 1)
      documentDetail.measurementUnit1Id = selectedMaterialAlterCode.measurementUnitId ?? '';
    else {
      let sortedSiblings = [...selectedMaterialAlterCode.allMaterialAlterCodes]
        .filter((x) => !!x.sequence)
        .sort((a, b) => (a.sequence || 0) - (b.sequence || 0));

      // Fallback: if no siblings have sequences, use all siblings sorted by isDefault first, then by id
      if (sortedSiblings.length === 0) {
        sortedSiblings = [...selectedMaterialAlterCode.allMaterialAlterCodes].sort((a, b) => {
          if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
          return (a.id || '').localeCompare(b.id || '');
        });
      }
      sortedSiblings = sortedSiblings.slice(0, totalUnits);
      if (sortedSiblings.length > 0) {
        documentDetail.measurementUnit1Id = sortedSiblings[0]?.measurementUnitId || '';
        documentDetail.relationFactor1 = sortedSiblings[0]?.relationFactor || 1;
      }
      if (sortedSiblings.length > 1) {
        documentDetail.measurementUnit2Id = sortedSiblings[1]?.measurementUnitId || '';
        documentDetail.relationFactor2 = sortedSiblings[1]?.relationFactor || 1;
      }
      if (sortedSiblings.length > 2) {
        documentDetail.measurementUnit3Id = sortedSiblings[2]?.measurementUnitId || '';
        documentDetail.relationFactor3 = sortedSiblings[2]?.relationFactor || 1;
      }
    }
    documentDetail.totalQuantity = calculateTotalQuantity(documentDetail);
    return documentDetail;
  }

  function calculateTotalQuantity(documentDetail: DocumentDetail): number {
    let totalQuantity = 0;
    if (documentDetail.quantity && documentDetail.measurementUnit1Id) {
      if (documentDetail.relationFactor1 && documentDetail.relationFactor1 > 0)
        totalQuantity += documentDetail.quantity * documentDetail.relationFactor1;
      else totalQuantity += documentDetail.quantity;
    }
    if (documentDetail.quantity2 && documentDetail.measurementUnit2Id) {
      if (documentDetail.relationFactor2 && documentDetail.relationFactor2 > 0)
        totalQuantity += documentDetail.quantity2 * documentDetail.relationFactor2;
      else totalQuantity += documentDetail.quantity2;
    }
    if (documentDetail.quantity3 && documentDetail.measurementUnit3Id) {
      if (documentDetail.relationFactor3 && documentDetail.relationFactor3 > 0)
        totalQuantity += documentDetail.quantity3 * documentDetail.relationFactor3;
      else totalQuantity += documentDetail.quantity3;
    }
    return totalQuantity;
  }

  async function generateLinesByRange(
    documentTypeId: string,
    customerId: string,
    traderSiteId: string
  ): Promise<DocumentDetail[]> {
    const rangeDetails = await getRangeDetailsByCustomerTraderSite(
      documentTypeId,
      customerId,
      traderSiteId
    );
    const documentType = await getDocumentTypeById(documentTypeId);
    if (rangeDetails !== null) {
      const result: DocumentDetail[] = [];

      for (const detail of rangeDetails) {
        const materialAlterCode = detail.materialAlterCode;
        if (
          !(
            materialAlterCode?.id &&
            materialAlterCode.alterCode?.name &&
            materialAlterCode.alterCode?.code &&
            materialAlterCode.materialId &&
            materialAlterCode.measurementUnit?.id
          )
        )
          continue; // Skip invalid entries

        let line = initNewDocumentLine();
        line.materialAlterCode = materialAlterCode;
        line.materialAlterCodeId = materialAlterCode.id;
        line.materialCode = materialAlterCode.alterCode.code;
        line.materialName = materialAlterCode.alterCode.name;
        line.materialId = materialAlterCode.materialId;
        line.unitPrice = detail.price;
        line.quantity = detail.quantity;
        if (detail.materialAlterCode.material) line.material = detail.materialAlterCode.material;

        line = processSiblingMaterialAlterCodes(
          line,
          detail.materialAlterCode,
          Math.min(documentType?.totalMeasurementUnits || 1, 3)
        );
        priceOrQuantityChange(line, 'quantity');

        if (detail.discounts) {
          line.appliedDiscounts = stringifyAppliedDiscounts(
            detail.discounts.map((d) => ({
              id: d.discountId,
              discountSequence: d.discountSequence,
              discountType: d.discountType,
              value: d.value,
            }))
          );

          const discountedPrice = applyDiscounts(
            line.unitPrice,
            detail.discounts.map((d) => ({
              id: d.discountId,
              discountSequence: d.discountSequence,
              discountType: d.discountType,
              value: d.value,
            }))
          );

          if (discountedPrice) line.unitPrice = discountedPrice;
        }

        if (detail.vatFactor && detail.vatFactorId) {
          line.vatFactorId = detail.vatFactorId;
          line.vatFactorValue = detail.vatFactor;
        } else await getMaterialVatFactor(line);

        result.push(line);
      }

      return result;
    }

    return [];
  }

  function initializeDynamicQuantities(
    detail: DocumentDetail,
    documentType?: Partial<DocumentType>
  ): DocumentDetail {
    const totalUnits = Math.min(documentType?.totalMeasurementUnits || 1, 3);

    if (totalUnits >= 2) {
      detail.quantity2 ??= 0;
      detail.measurementUnit2Id ||= '';
    } else {
      detail.quantity2 = undefined;
      detail.measurementUnit2Id = undefined;
    }

    if (totalUnits >= 3) {
      detail.quantity3 ??= 0;
      detail.measurementUnit3Id ||= '';
    } else {
      detail.quantity3 = undefined;
      detail.measurementUnit3Id = undefined;
    }

    return detail;
  }

  function getDynamicQuantityFields(
    detail: DocumentDetail,
    documentType?: Partial<DocumentType>,
    measurementUnits?: MeasurementUnit[]
  ): DynamicQuantityField[] {
    const totalUnits = Math.min(documentType?.totalMeasurementUnits || 1, 3);
    const fields: DynamicQuantityField[] = [];

    fields.push({
      unitIndex: 1,
      quantity: detail.quantity,
      measurementUnitId: detail.measurementUnit1Id,
      label: measurementUnits?.find((x) => x.id === detail.measurementUnit1Id)?.code ?? '',
    });

    if (totalUnits >= 2) {
      fields.push({
        unitIndex: 2,
        quantity: detail.quantity2 || 0,
        measurementUnitId: detail.measurementUnit2Id || '',
        label: measurementUnits?.find((x) => x.id === detail.measurementUnit2Id)?.code ?? '',
      });
    }

    if (totalUnits >= 3) {
      fields.push({
        unitIndex: 3,
        quantity: detail.quantity3 || 0,
        measurementUnitId: detail.measurementUnit3Id || '',
        label: measurementUnits?.find((x) => x.id === detail.measurementUnit3Id)?.code ?? '',
      });
    }
    return fields;
  }

  return {
    salesDocument,
    modifiedSalesDocument,
    docDetailLines,
    rangeLineIds,
    isModified,
    clearForm,
    clearDetails,
    customerPricingPolicy,
    activeFilters,
    setActiveFilters,
    addFilter,
    removeFilter,
    clearAllFilters,
    searchMaterialAlterCodes,
    getMaterialPropertiesFromCustomerPricingPolicy,
    getMaterialDiscountsFromCustomerPricingPolicy,
    getMaterialVatFactor,
    updateDocLines,
    priceOrQuantityChange,
    discountValueChange,
    createLineRules,
    createLineValidationModel,
    discountPercentageChange,
    grossTotalChange,
    netTotalChange,
    initNewDocumentLine,
    saveDocumentLineInStore,
    updateLineDiscounts,
    generateLinesByRange,
    calculateNetTotal,
    calculateLineVatTotal,
    calculateGrossTotal,
    updateHeaderTotals,
    calculateTotalBeforeDiscount,
    vatFactorChanged,
    docLineQuantityRule,
    docLineUnitPriceRule,
    priceZeroRule,
    materialActiveCatalogRule,
    discountNotMoreThanUnitPrice,
    updateExistsNotExistsFlags,
    hasModifiedDetails,
    processSiblingMaterialAlterCodes,
    calculateTotalQuantity,
    getDynamicQuantityFields,
    initializeDynamicQuantities,
  };
});
